/**
 * Soul AI Design System
 * Supports light and dark modes
 */

// ─── Shared values (same in both themes) ─────────────────────────────────────

const common = {
  typography: {
    fontFamily: {
      primary: "'Hanken Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      secondary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    fontSize: {
      xs: '10px',
      sm: '12px',
      base: '14px',
      md: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
    },
    fontWeight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.4,
      relaxed: 1.6,
      loose: 2,
    },
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '32px',
    '4xl': '48px',
  },
  borderRadius: {
    sm: '8px',
    md: '10px',
    lg: '16px',
    xl: '20px',
    full: '100px',
    message: '9.528px',
  },
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.15)',
  },
  transitions: {
    fast: '150ms ease-in-out',
    normal: '250ms ease-in-out',
    slow: '350ms ease-in-out',
  },
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    modal: 1300,
    popover: 1400,
    tooltip: 1500,
  },
  breakpoints: {
    mobile: '375px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1280px',
  },
} as const;

// ─── Dark Theme (existing) ───────────────────────────────────────────────────

const darkColors = {
  primary: {
    main: '#834CC9',
    light: '#DC9CFF',
    dark: '#5C3589',
  },
  secondary: {
    lavenderMagenta: '#DC9CFF',
    fennelFlower: '#72A5FF',
  },
  neutral: {
    white: '#FFFFFF',
    whiteOpacity8: 'rgba(255, 255, 255, 0.08)',
    whiteOpacity20: 'rgba(255, 255, 255, 0.2)',
    gray100: '#DFE0E2',
    gray200: '#C5C6CA',
    gray300: '#B0B0B0',
    charcoal: '#222222',
    dark: '#535353',
  },
  chat: {
    userBg: 'rgba(133, 82, 199, 0.2)',
    assistantText: '#DFE0E2',
    inputBg: 'rgba(255, 255, 255, 0.08)',
    inputBorder: 'rgba(255, 255, 255, 0.2)',
  },
  background: {
    main: '#222222',
    card: 'rgba(255, 255, 255, 0.08)',
  },
  calorie: {
    progress: '#4CAF50',
    remaining: '#834CC9',
    over: '#F44336',
    macros: {
      carbs: '#FF6B6B',
      protein: '#FF8BD1',
      fat: '#FFD93D',
    },
  },
} as const;

// ─── Light Theme (new) ──────────────────────────────────────────────────────

const lightColors = {
  primary: {
    main: '#834CC9',
    light: '#A06FDB',
    dark: '#5C3589',
  },
  secondary: {
    lavenderMagenta: '#9B6DD7',
    fennelFlower: '#5B8FE8',
  },
  neutral: {
    white: '#1A1A2E',          // primary text (dark)
    whiteOpacity8: 'rgba(0, 0, 0, 0.05)',  // subtle bg elevation
    whiteOpacity20: 'rgba(0, 0, 0, 0.08)', // borders
    gray100: '#2C2C3E',       // strong text
    gray200: '#555570',       // secondary text
    gray300: '#8888A0',       // muted text
    charcoal: '#F7F7FA',      // base background
    dark: '#D0D0DA',          // surface dark (scrollbar etc)
  },
  chat: {
    userBg: 'rgba(131, 76, 201, 0.08)',
    assistantText: '#2C2C3E',
    inputBg: '#FFFFFF',
    inputBorder: 'rgba(0, 0, 0, 0.1)',
  },
  background: {
    main: '#F7F7FA',
    card: '#FFFFFF',
  },
  calorie: {
    progress: '#4CAF50',
    remaining: '#834CC9',
    over: '#F44336',
    macros: {
      carbs: '#FF6B6B',
      protein: '#FF8BD1',
      fat: '#FFD93D',
    },
  },
} as const;

// ─── Exports ─────────────────────────────────────────────────────────────────

export const darkTheme = { ...common, colors: darkColors };
export const lightTheme = { ...common, colors: lightColors };

// Default export for backward compatibility (used by all existing styled components)
export const theme = darkTheme;

// Recursive type helper to widen readonly string literals to string
type DeepWritable<T> = { -readonly [P in keyof T]: T[P] extends object ? DeepWritable<T[P]> : T[P] extends string ? string : T[P] };

export type Theme = DeepWritable<typeof darkTheme>;
